import React from 'react';
import { Box, Typography, Link, Grid, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';

const CreditsContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(4),
  marginTop: theme.spacing(4),
  borderRadius: 12,
  border: '3px dashed #ffd4c8',
}));

const ArtistLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.secondary,
  textDecoration: 'none',
  '&:hover': {
    color: theme.palette.primary.light,
    textDecoration: 'underline',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontFamily: 'FranxurterTotallyMedium',
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(3),
}));

const CreditItem = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(2),
  borderRadius: 8,
  border: '2px dashed #ffd4c8',
  marginBottom: theme.spacing(2),
}));

const Credits = () => {
  const artists = [
    { name: 'Erukuron', platform: 'Twitter', handle: 'Erukuron' },
    { name: 'Akaifuwa', platform: 'Twitter', handle: 'Akaifuwa' },
    { name: 'amyoh', platform: 'Bluesky', handle: 'amyoh.bsky.social' },
    { name: 'MimiWitchhazel', platform: 'Twitter', handle: 'MimiWitchhazel' },
    { name: 'PandawaVT', platform: 'Twitter', handle: 'PandawaVT' },
    { name: 'vulpiepie', platform: 'Twitter', handle: 'vulpiepie' },
    { name: 'chronbuu', platform: 'Twitter', handle: 'chronbuu' },
    { name: 'YuureiChanto', platform: 'Twitter', handle: 'YuureiChanto' },
    { name: 'real_sinistral', platform: 'Twitter', handle: 'real_sinistral' },
    { name: 'LumiCapis', platform: 'Twitter', handle: 'LumiCapis' },
    { name: 'duckduckdduk', platform: 'Twitter', handle: 'duckduckdduk' },
    { name: 'Mnaflzero', platform: 'VGen', handle: 'Mnaflzero' },
    { name: 'Lilundere', platform: 'Twitter', handle: 'Lilundere' },
    { name: 'H00T_1O31', platform: 'Twitter', handle: 'H00T_1O31' },
    { name: 'stickeemochee', platform: 'VGen', handle: 'stickeemochee' },
    { name: '__s7149', platform: 'Twitter', handle: '__s7149' },
    { name: 'Smurfiiee1', platform: 'Twitter', handle: 'Smurfiiee1' },
    { name: 'RuesArchiive', platform: 'Twitter', handle: 'RuesArchiive' },
    { name: 'MoonreiVT', platform: 'Twitter', handle: 'MoonreiVT' },
  ];

  const overlaysAndAssets = [
    {
      title: 'Overlays, Schedule & Logo',
      creator: 'almosthirteen',
      platform: 'Twitter',
      handle: 'almosthirteencredits',
      images: [
        '/credits/CHATTING OVERLAY.png',
        '/credits/SCHEDULE.png',
        '/credits/LOGO-4.jpg'
      ]
    }
  ];

  const models = [
    {
      title: 'Runerek 1.0',
      creator: 'RuesArchiives',
      platform: 'Twitter',
      handle: 'RuesArchiives',
      role: 'Created by',
      images: [
        '/credits/runechibi2.png',
        '/credits/Rune_UPDATED_sheet_with_logo2023.png'
      ]
    },
    {
      title: 'Runerek 2.0 (Runita)',
      designer: 'RuesArchiives',
      creator: 'Vulpiepie',
      designerHandle: 'RuesArchiives',
      creatorHandle: 'vulpiepie',
      images: [
        '/credits/Character_Sheet.png'
      ]
    },
    {
      title: "Snake's 1.0",
      creator: 'RuesArchiives',
      platform: 'Twitter',
      handle: 'RuesArchiives',
      role: 'Created by',
      images: [
        '/credits/RuneBFSheet.png'
      ]
    }
  ];

  const getArtistUrl = (artist) => {
    switch (artist.platform) {
      case 'Twitter':
        return `https://twitter.com/${artist.handle}`;
      case 'Bluesky':
        return `https://bsky.app/profile/${artist.handle}`;
      case 'VGen':
        return `https://vgen.co/${artist.handle}`;
      default:
        return '#';
    }
  };

  const getUrlFromHandle = (handle) => {
    return `https://x.com/${handle}`;
  };

  return (
    <CreditsContainer>
      <Typography 
        variant="h4" 
        component="h2" 
        gutterBottom 
        sx={{ 
          textAlign: 'center', 
          color: 'text.secondary',
          fontFamily: 'FranxurterTotallyMedium',
          marginBottom: 3
        }}
      >
        Credits
      </Typography>
      
      <Typography 
        variant="body1" 
        sx={{ 
          textAlign: 'center', 
          marginBottom: 3,
          fontStyle: 'italic'
        }}
      >
        Thank you to everyone who has contributed to bringing Runerek to life!
      </Typography>

      <Divider sx={{ marginY: 3, borderColor: '#ffd4c8' }} />

      {/* Overlays and Assets Section */}
      <SectionTitle variant="h5" component="h3">
        🎨 Overlays & Assets
      </SectionTitle>
      
      {overlaysAndAssets.map((item, index) => (
        <CreditItem key={index}>
          <Typography variant="h6" sx={{ color: 'text.secondary', marginBottom: 1 }}>
            {item.title}
          </Typography>
          <Typography variant="body2" sx={{ marginBottom: 1 }}>
            Made by{' '}
            <ArtistLink
              href={getUrlFromHandle(item.handle)}
              target="_blank"
              rel="noopener noreferrer"
            >
            {item.creator}
            </ArtistLink>
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, marginTop: 1 }}>
            {item.images.map((image, imgIndex) => (
              <Box
                key={imgIndex}
                component="img"
                src={image}
                alt={`${item.title} ${imgIndex + 1}`}
                sx={{
                  maxWidth: 150,
                  maxHeight: 150,
                  objectFit: 'contain',
                  border: '1px solid #ffd4c8',
                  borderRadius: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }}
              />
            ))}
          </Box>
        </CreditItem>
      ))}

      <Divider sx={{ marginY: 3, borderColor: '#ffd4c8' }} />

      {/* Models Section */}
      <SectionTitle variant="h5" component="h3">
        🎭 Models
      </SectionTitle>
      
      {models.map((model, index) => (
        <CreditItem key={index}>
          <Typography variant="h6" sx={{ color: 'text.secondary', marginBottom: 1 }}>
            {model.title}
          </Typography>
          {model.designer && model.creator ? (
            <Typography variant="body2" sx={{ marginBottom: 1 }}>
              Designed by{' '}
              <ArtistLink
                href={getUrlFromHandle(model.designerHandle)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {model.designer}
              </ArtistLink>
              {' • '}
              Created by{' '}
              <ArtistLink
                href={`https://twitter.com/${model.creatorHandle}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {model.creator}
              </ArtistLink>
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ marginBottom: 1 }}>
              {model.role}{' '}
              <ArtistLink
                href={getUrlFromHandle(model.handle)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {model.creator}
              </ArtistLink>
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, marginTop: 1 }}>
            {model.images.map((image, imgIndex) => (
              <Box
                key={imgIndex}
                component="img"
                src={image}
                alt={`${model.title} ${imgIndex + 1}`}
                sx={{
                  maxWidth: 200,
                  maxHeight: 200,
                  objectFit: 'contain',
                  border: '1px solid #ffd4c8',
                  borderRadius: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }}
              />
            ))}
          </Box>
        </CreditItem>
      ))}

      <Divider sx={{ marginY: 3, borderColor: '#ffd4c8' }} />

     
    </CreditsContainer>
  );
};

export default Credits;